import { Anomaly, AnomalyApi, RequestPostAnomaly } from '@api/twin/AnomalyApi';
import { useTeiaViewerContext } from '@stereograph/teiaviewer';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { usePluginTranslation } from '@translation';
import { anomalyQueryKeys } from '../query-keys';

export const usePostAnomaly = (projectId: number) => {
  const { t } = usePluginTranslation();
  const { viewer } = useTeiaViewerContext();
  const client = viewer.twinApiClient;
  const queryClient = useQueryClient();

  const { mutateAsync: postAnomaly, isPending }
    = useMutation<
      Anomaly,
      Error,
      RequestPostAnomaly,
      string | undefined
    >({
      mutationFn: async (anomaly) => {
        return await AnomalyApi(client).postAnomaly(projectId, anomaly);
      },
      onMutate: () => {
        return viewer.notificationCenter.pushNotification({
          type: t('anomaly_plugin.notification_type_text'),
          status: 'pending',
          message: t('anomaly_plugin.widget.form.submit.create.loading.text'),
          title: t('anomaly_plugin.widget.form.submit.create.notification_title'),
          displayed: true,
        });
      },
      onSuccess: async (data, _variables, notificationId) => {
        await queryClient.invalidateQueries({
          queryKey: anomalyQueryKeys.anomalies_default(projectId),
        });
        viewer.notificationCenter.pushNotification({
          type: t('anomaly_plugin.notification_type_text'),
          status: 'success',
          message: t('anomaly_plugin.widget.form.submit.create.success.text', {
            title: data.title,
          }),
          title: t('anomaly_plugin.widget.form.submit.create.notification_title'),
          displayed: true,
        }, notificationId);
      },
      onError: (_response, variable, notificationId) => {
        viewer.notificationCenter.pushNotification({
          type: t('anomaly_plugin.notification_type_text'),
          status: 'error',
          message: t('anomaly_plugin.widget.form.submit.create.error.text', {
            title: variable.title,
          }),
          title: t('anomaly_plugin.widget.form.submit.create.notification_title'),
          displayed: true,
        }, notificationId);
      },
    });

  return {
    postAnomaly,
    isPending,
  };
};
